/**
 * Ligation set-up: insert mass for a chosen molar ratio, and the reaction recipe.
 *
 * Insert mass (ng) = ratio × vector mass (ng) × insert length ÷ vector length, where the
 * ratio is insert parts per one part vector (a "1:3 vector:insert" ligation is 3). That is
 * NEB's published formula; it depends only on the length ratio, so the choice of average
 * base-pair mass changes the pmol figures but never the insert mass.
 *
 * Amounts in pmol use the average mass of a base pair: 660 g/mol (the common convention) or
 * 617.96 g/mol (NEB's published average), which is what makes NEB's 50 ng of a 4 kb vector
 * come out at 0.020 pmol.
 */

export type BpMass = "660" | "617.96";

export const BP_MASS_VALUES: Record<BpMass, number> = {
  "660": 660,
  "617.96": 617.96,
};

const PMOL_PER_NG_PER_MW = 1000;
/** NEB: overall DNA concentration of 1–10 ng/µL for an efficient ligation. */
export const MIN_TOTAL_NG_PER_UL = 1;
export const MAX_TOTAL_NG_PER_UL = 10;
/** NEB: 20–30 fmol of vector. */
export const MIN_VECTOR_FMOL = 20;
export const MAX_VECTOR_FMOL = 30;

export type LigationError =
  | "vectorLength"
  | "insertLength"
  | "vectorMass"
  | "ratio"
  | "volume"
  | "buffer";

export interface LigationInput {
  vectorLength: number;
  vectorNg: number;
  insertLength: number;
  /** Insert parts per one part vector. */
  ratio: number;
  bpMass: BpMass;
  vectorConc: number | null;
  insertConc: number | null;
  totalUl: number;
  ligaseUl: number;
  /** Strength of the buffer stock: 10 for a 10X buffer. */
  bufferStock: number;
}

export interface LigationRecipe {
  bufferUl: number;
  vectorUl: number | null;
  insertUl: number | null;
  ligaseUl: number;
  waterUl: number | null;
}

export interface LigationResult {
  insertNg: number;
  vectorPmol: number;
  insertPmol: number;
  totalNg: number;
  totalNgPerUl: number;
  recipe: LigationRecipe;
  /** Water would be negative: the DNA, buffer and ligase do not fit. */
  overfull: boolean;
}

export function pmolOf(ng: number, length: number, bpMass: BpMass): number {
  return (ng * PMOL_PER_NG_PER_MW) / (BP_MASS_VALUES[bpMass] * length);
}

/** Mass of insert (ng) that gives `ratio` moles of insert per mole of vector. */
export function insertMass(
  vectorNg: number,
  vectorLength: number,
  insertLength: number,
  ratio: number
): number {
  return (ratio * vectorNg * insertLength) / vectorLength;
}

function volumeFor(ng: number, conc: number | null): number | null {
  return conc !== null && conc > 0 ? ng / conc : null;
}

function validate(input: LigationInput): LigationError | null {
  if (!(input.vectorLength > 0)) {
    return "vectorLength";
  }
  if (!(input.insertLength > 0)) {
    return "insertLength";
  }
  if (!(input.vectorNg > 0)) {
    return "vectorMass";
  }
  if (!(input.ratio > 0)) {
    return "ratio";
  }
  if (!(input.totalUl > 0) || input.ligaseUl < 0) {
    return "volume";
  }
  return input.bufferStock >= 1 ? null : "buffer";
}

export function planLigation(
  input: LigationInput
): { result: LigationResult } | { error: LigationError } {
  const error = validate(input);
  if (error) {
    return { error };
  }
  const insertNg = insertMass(
    input.vectorNg,
    input.vectorLength,
    input.insertLength,
    input.ratio
  );
  const bufferUl = input.totalUl / input.bufferStock;
  const vectorUl = volumeFor(input.vectorNg, input.vectorConc);
  const insertUl = volumeFor(insertNg, input.insertConc);
  const known = vectorUl !== null && insertUl !== null;
  const waterUl = known
    ? input.totalUl - bufferUl - input.ligaseUl - vectorUl - insertUl
    : null;
  const totalNg = input.vectorNg + insertNg;
  return {
    result: {
      insertNg,
      vectorPmol: pmolOf(input.vectorNg, input.vectorLength, input.bpMass),
      insertPmol: pmolOf(insertNg, input.insertLength, input.bpMass),
      totalNg,
      totalNgPerUl: totalNg / input.totalUl,
      recipe: {
        bufferUl,
        vectorUl,
        insertUl,
        ligaseUl: input.ligaseUl,
        waterUl,
      },
      overfull: waterUl !== null && waterUl < 0,
    },
  };
}
